import { CanvasObject }           from './canvas.object';
import { VirtualCanvas }          from './virtual.canvas';
import { Track }                  from '@/models/scenario/track';
import { Logger }                 from '@/util/logger';
import { Rectangle }              from './visuals/rectangle';
import { VisualFactory }          from './visuals/visual.factory';
import { TextOptions }            from '@/helpers/fonts/text.options';
import { FontWeight }             from '@/helpers/fonts/font.weight';
import { TextMeasure }            from '@/helpers/fonts/text.measure';
import { BlockTitle }             from './block.title';
import { TrackLabel }             from './track.label';
import { StickySide }             from './sticky.side';
import { Orientation }            from './orientation';
import { Port }                   from './port';
import { HighLighterInterface }   from './high.lighter.interface';
import { HighLightableInterface } from './high.lightable.interface';
import { HighLightInterface }     from './high.light.interface';

export enum LinkBlockOrientation {
  LEFT   = 'left',
  RIGHT  = 'right',
  TOP    = 'top',
  BOTTOM = 'bottom'
}

/**
 *
 * @class LinkBlock
 *
 * top level object that can be used for east, west and junction blocks in our
 * connection viewer.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class LinkBlock extends CanvasObject implements HighLightableInterface {

  /** the current highlight if there is one */
  highLightStyle: HighLightInterface|null   = null;

  /* styling */

  /** transparency */
  public opacity: number                    = 1.0;

  /** visibility */
  public visible: boolean                   = true;

  /** fill color */
  public backgroundColor: string            = '#36454F';

  /** outline color */
  public borderColor: string                = 'dimgrey';

  /** how thick an outline to use */
  public borderWidth: number                = 5;

  /** internal margin */
  public margin: number                     = 12;

  /** rounding for the outline */
  public cornerRadius: number               = 10;

  /** the font size for the title label */
  public titleSize: number                  = 14;

  /** the font size for track labels */
  public trackSize: number                  = 18;

  /** the padding inside the outline */
  public padding: number                    = 8;

  /** the spacing between track labels */
  public trackSpacing: number               = 24;

  /** the title text for the block */
  public title: string                      = 'BLOCK'

  /** the tracks that this block visualizes */
  private readonly tracks: Track[]          = [];

  /** is which side of this canvas does this block stick to? */
  public orientation: LinkBlockOrientation = LinkBlockOrientation.LEFT;

  /** the width needed by the track labels */
  private trackArrayWidth: number          = 64;

  /** the height needed by the track labels */
  private trackArrayHeight: number         = 14;

  /** the maximum width of the labels */
  private labelWidth: number               = 64;

  /** the width of the title */
  private titleWidth: number              = 64;

  /** background rectangle for this block */
  private outline: Rectangle|null          = null;

  /**
   *
   * @param canvas      VirtualCanvas all objects must render to a canvas
   * @param highlighter The HighLightManager we can use for highlighting and style overrides
   * @param tracks      Track[] you must provide the tracks that this block will be visualizing
   * @param config      Partial<LinkBlock> allows for overriding the styling attributes
   */
  constructor(
    canvas:      VirtualCanvas,
    highlighter: HighLighterInterface|null = null,
    tracks:      Track[]                   = [],
    config:      Partial<LinkBlock>        = {}) {

    super(canvas, false);

    this.tracks      = tracks;

    /* use any overrides */

    Object.assign(this, config);

    Logger.debug(`LinkBlock ctor() orientation=${this.orientation}, tracks:`, this.tracks, ' config: ', config);

    /* make the base visual for the block */

    if (!this.canvas()) {

      return ;
    }

    /* make the outline */

    this.outline = VisualFactory.buildRectangle(this.outlineConfig());

    if (this.outline) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.outline.handle = this.canvas()?.addShape(this.outline);

      this.boundUpdate({
        x:      this.outline.x      ?? 0,
        y:      this.outline.y      ?? 0,
        width:  this.outline.width  ?? 10,
        height: this.outline.height ?? 10
      });

    }

    /*
     * make the block title, title is always at the top, unless the block is at
     * the bottom of the window; because tracks and connections are in the center
     * of the window.
     *
     */

    let sticky: StickySide = StickySide.TOP;

    if (this.orientation == LinkBlockOrientation.BOTTOM) {

      sticky = StickySide.BOTTOM;
    }

    new BlockTitle(this, {
      text:     this.title,
      sticky,
      size:     this.titleSize,
      color:    'white'
    });

    /* by default, order the tracks by track number */

    /* this.sortTracksByNumber(); */

    this.sortTracksReverseNameThenNumber();

    /* make the track labels */

    let orientation: Orientation = Orientation.RIGHT;
    let verticalOffset: number   = 0;

    if (this.orientation == LinkBlockOrientation.LEFT) {

      /* tracks must point inwards to the right */

      orientation    = Orientation.RIGHT;
      verticalOffset = this.padding + this.titleSize * 3;

    } else if (this.orientation == LinkBlockOrientation.RIGHT) {

      orientation    = Orientation.LEFT;
      verticalOffset = this.padding + this.titleSize * 3;

    } else if (this.orientation == LinkBlockOrientation.TOP) {

      orientation    = Orientation.BOTTOM;

    }  else if (this.orientation == LinkBlockOrientation.BOTTOM) {

      orientation    = Orientation.TOP;
      verticalOffset = this.height - this.padding;
    }

    this.tracks.map((track: Track, order: number): TrackLabel => {

      return new TrackLabel(this, highlighter, {
        track,
        orientation,
        size:         this.trackSize,
        color:        'white',
        order,
        titlesWidth:  this.trackArrayWidth,
        titlesHeight: this.trackArrayHeight,
        padding:      this.padding,
        spacing:      this.trackSpacing,
        verticalOffset
      });

    });

  }

  /** fetch all the track label objects for this block. */
  public labels(): TrackLabel[] {

    return this.children().filter((x: any): boolean => x instanceof TrackLabel).map((x: CanvasObject): TrackLabel => (x as TrackLabel));
  }

  /** look up the track label of this link (the Track ID as it is in the JSON) */
  public labelById(id: number): TrackLabel|null {

    return this.children().find((child: CanvasObject): boolean => {

      return (child as TrackLabel).track?._id == id;

    }) as TrackLabel ?? null;
  }

  /**
   *
   * convenience to find the port for the given Track.  You can also use
   * the track's ID as given in the JSON.
   *
   */
  public portForTrack(id: number | Track): Port|null {

    /* if they gave a Track, convert to its ID */

    if (id instanceof Track) {

      id = id._id;
    }

    /* lookup by track id */

    let label: TrackLabel|null = this.labelById(id);

    if (!label) {

      /* that track is not in this block */

      return null;
    }

    /* track labels have only one child...the port */

    return label.first() as Port;
  }

  /** helper to sort the tracks by track number */
  private sortTracksByNumber(): LinkBlock {

    this.tracks.sort((a: Track, b: Track): number => {

      return a._track - b._track;
    });

    return this;
  }

  /** helper to sort the tracks first by name (reverse so P is at the top) and then by number */
  private sortTracksReverseNameThenNumber(): LinkBlock {

    this.tracks.sort((a: Track, b: Track): number => {

      let aName: string = Track.typeToString(a._type);
      let bName: string = Track.typeToString(b._type);

      /* sort first by name (reverse, we want parking tracks at the top */

      if (aName != bName) {

        return bName.charCodeAt(0) - aName.charCodeAt(0);
      }

      /* ok, sort by track number */

      return a._track - b._track;
    });

    return this;
  }

  /** generate the configuration for the base visual of the block */
  private outlineConfig(): Partial<Rectangle> {

    /* figure out what the internal bound required is for the text we will need to show */

    let minimumWidth: number;
    let minimumHeight: number;

    /* internal labels are the title (top of the block) and the labels for each track. */

    let title: TextOptions = new TextOptions({
      text:   this.title,
      size:   this.titleSize,
      weight: FontWeight.BOLD,
    });

    this.titleWidth        = TextMeasure.measure(title.text, title.format()).width;

    this.trackArrayHeight  = ((this.trackSize + this.trackSpacing) * this.tracks.length);

    if (this.orientation == LinkBlockOrientation.LEFT || this.orientation == LinkBlockOrientation.RIGHT) {

      /* if orientation is left or righter than the text is horizontal, but stacked vertically */

      /* have to be wide enough for the largest track name */

      let trackText: TextOptions = new TextOptions({
        text:   'XO',
        size:   this.trackSize,
        weight: FontWeight.BOLD,
      });

      let labelWidths: number[] = this.tracks.map((x: Track): number => {

        trackText.text = TrackLabel.trackName(x, this.orientation == LinkBlockOrientation.RIGHT);

        return TextMeasure.measure(trackText.text, trackText.format()).width;
      });

      this.labelWidth = Math.max(...labelWidths);

      this.trackArrayWidth  = this.labelWidth;

    } else {

      /* the text will be vertical, but appended horizontally */

      /* have to be at least high enough for the title ... */

      /* have to be wide enough for the largest track name */

      let trackText: TextOptions = new TextOptions({
        text:   'XO',
        size:   this.trackSize,
        weight: FontWeight.BOLD,
      });

      let labelWidths: number[] = this.tracks.map((x: Track): number => {

        trackText.text = TrackLabel.trackName(x);

        return TextMeasure.measure(trackText.text, trackText.format()).width;
      });

      this.labelWidth = Math.max(...labelWidths);

      this.trackArrayWidth  = this.labelWidth;

    }

    /* create the outline visual */

    let opacity: number            = this.opacity;
    let visible: boolean           = this.visible;
    let backgroundColor: string    = this.backgroundColor;
    let borderColor: string        = this.borderColor;
    let borderWidth: number        = this.borderWidth;
    let margin: number             = this.margin;
    let cornerRadius: number       = this.cornerRadius;
    let width: number;
    let height: number;
    let x: number                  = margin;
    let y: number                  = (this.canvas()?.height ?? 0) * 0.3;

    /* double check against minimum size, we can't shrink past the size of text to show inside */

    if (this.orientation == LinkBlockOrientation.LEFT || this.orientation == LinkBlockOrientation.RIGHT) {

      minimumWidth       = this.padding + Math.max(this.labelWidth, this.titleWidth) + this.padding;
      minimumHeight      = this.padding + this.titleSize * 3 + this.trackArrayHeight + this.padding;

    } else {

      minimumWidth       = this.padding + Math.max(this.titleWidth, this.trackArrayHeight) + this.padding;
      minimumHeight      = this.padding + this.titleSize * 3 + this.labelWidth + this.padding;

    }

    width  = Math.max(minimumWidth);
    height = Math.max(minimumHeight);

    if (this.orientation == LinkBlockOrientation.BOTTOM || this.orientation == LinkBlockOrientation.TOP) {

      x  = ((this.canvas()?.width ?? 0) / 2) - (width / 2)
    }

    if (this.orientation == LinkBlockOrientation.RIGHT) {

      x = (this.canvas()?.width ?? 0) - width - margin;
    }

    if (this.orientation == LinkBlockOrientation.TOP) {

      y = margin;
    }

    if (this.orientation == LinkBlockOrientation.BOTTOM) {

      y = (this.canvas()?.height ?? 0) - height - margin;
    }

    /* don't let the left or right blocks float up higher than the top block */

    if (this.orientation == LinkBlockOrientation.LEFT || this.orientation == LinkBlockOrientation.RIGHT) {

      y = Math.max(y,  (this.canvas()?.height ?? 0) * 0.3, 150);
    }

    /* pass back the specification for the outline */

    return {
      x,
      y,
      width,
      height,
      cornerRadius,
      backgroundColor,
      borderColor,
      borderWidth,
      visible,
      opacity
    };

  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /*
     * the screen geometry has changed somehow, so we have to recalculate our layout and
     * then get the canvas visuals to update in place.
     *
     */

    let handle: any = this.outline?.handle ?? null;

    if (!handle) {

      /* nothing to do layout on yet */

      return this;
    }

    this.outline        = VisualFactory.buildRectangle(this.outlineConfig());
    this.outline.handle = handle;

    this.boundUpdate({
      x:      this.outline.x      ?? 0,
      y:      this.outline.y      ?? 0,
      width:  this.outline.width  ?? 10,
      height: this.outline.height ?? 10
    });

    this.canvas()?.updateShape(this.outline);

    /* make sure the children get nudged as needed */

    this.children().forEach((item: CanvasObject): void => {

      item.layout();

    });

    return this;
  }

  /**
   * enable or disable highlighting.  When not null, set highLightStyle to
   * the new style, and object should apply any applicable style overrides.
   * When null, highLightStyle should be cleared, and the object should
   * revert to its normal styles.
   *
   * @param style the highlight to apply, or clear the current highlight.
   */
  public highLight(style: HighLightInterface|null): void {

    /* we don't yet do any highlighting of the blocks */

  }



}
